import { useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import { animationScrollToTop } from '../../utils';
import TopicsFilter from '../TopicsFilter';
import MessagesTopicsTableHead from '../MessagesTopicsTable/components/MessagesTopicsTableHead';
import i18nContext from 'components/i18n-context';
import Pagination from 'components/Pagination/Pagination';
import Button from 'uikit/Button/Button';
import { getCustomerLink } from 'services/utils';
import { getCustomerInfoRequest } from 'services/requestAgent';
import './Topics.scss';

const MESSAGE_TOPIC_STATUS = {
  OPEN: 'OPEN',
  CLOSED: 'CLOSED',
  IN_PROGRESS: 'IN_PROGRESS'
};

const Topics = ({
  topics,
  activeTopicIndex,
  handleChangeActiveTopic,
  handleClickNewMessageButton,
  topicsRef,
  showWriteMessageButton,
  messagesStore
}) => {
  const i18n = useContext(i18nContext);
  const navigate = useNavigate();

  useEffect(() => {
    if (topics) {
      animationScrollToTop(topicsRef);
    }
  }, [topics, topicsRef]);

  const selectTopic = (topicId) => {
    handleChangeActiveTopic(topicId);
  };

  const handleOnSortTable = (sortBy) => {
    messagesStore.setSortData({
      sortBy,
      direction:
        messagesStore.sortColumn.sort_column === sortBy && messagesStore.sortColumn.sort_direction === 'ASC'
          ? 'DESC'
          : 'ASC'
    });
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case MESSAGE_TOPIC_STATUS.OPEN:
        return {
          label: 'Open',
          className: 'status-open',
          color: 'var(--system-colours-success-default, #20C55D)'
        };
      case MESSAGE_TOPIC_STATUS.IN_PROGRESS:
        return {
          label: 'In progress',
          className: 'status-in-progress',
          color: 'var(--element-graphic-acent, #8F27C7)'
        };
      case MESSAGE_TOPIC_STATUS.CLOSED:
        return {
          label: 'Closed',
          className: 'status-closed',
          color: 'var(--element-graphic-tertiary, #7F8DA0)'
        };
      default:
        return {
          label: 'Open',
          className: 'status-open',
          color: 'var(--system-colours-success-default, #20C55D)'
        };
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const today = new Date();

    // Check if it's today
    if (date.toDateString() === today.toDateString()) {
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
    }
    // Otherwise return date in dd.mm.yy format
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' }).replace(/\//g, '.');
  };

  const handlePageChange = (page) => {
    messagesStore.setPage(page);
    animationScrollToTop(topicsRef);
  };

  const handleRedirectToCustomer = async (e, customerNumber) => {
    e.stopPropagation();
    if (!customerNumber) return;

    try {
      const customer = await getCustomerInfoRequest(customerNumber);
      const customerUrl = getCustomerLink(customerNumber, customer.status, customer.type);
      window.open(customerUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to redirect to customer:', error);
    }
  };

  return (
    <div className={'topics-container'}>
      {showWriteMessageButton && (
        <Button
          className={'write-new-message-button'}
          type={'primary'}
          onClick={handleClickNewMessageButton}
          roleType={'button'}
          size={'large'}
        >
          <span>{i18n.getMessage('messages.button.writeNewMessage') || 'Write a message'}</span>
        </Button>
      )}

      <TopicsFilter />
      <div className={'topics-list'}>
        {topics && topics.length > 0 ? (
          <>
            {/* Table with Header and Body */}
            <table className={'topics-table'}>
              <thead>
                <MessagesTopicsTableHead
                  handleOnSortTable={handleOnSortTable}
                  sortBy={messagesStore.sortColumn.sort_column}
                  direction={messagesStore.sortColumn.sort_direction?.toLowerCase()}
                />
              </thead>
              <tbody ref={topicsRef} className={'topics-wrapper'}>
                {topics.map((topic, index) => {
                  const statusConfig = getStatusConfig(topic.status);
                  const isActive = activeTopicIndex === index;
                  const hasNewMessage = !topic.readByOperator;
                  const time = formatTime(topic?.lastMessageCreatedAt || topic?.createdAt);

                  return (
                    <tr
                      key={topic.id || index}
                      className={`topic-row ${isActive ? 'active' : ''} ${hasNewMessage ? 'unread' : ''}`}
                      onClick={() => selectTopic(topic.id)}
                      onKeyDown={(e) => e.key === 'Enter' && selectTopic(topic.id)}
                      role={'button'}
                      tabIndex={0}
                    >
                      <td className={'topic-cell topic-status-cell'}>
                        <div className={`topic-status ${statusConfig.className}`}>
                          <div className={'status-dot'} style={{ backgroundColor: statusConfig.color }} />
                          <span className={'status-label'} style={{ color: statusConfig.color }}>
                            {statusConfig.label}
                          </span>
                        </div>
                      </td>
                      <td className={'topic-cell topic-subject-cell'}>
                        <span className={'topic-subject'}>{topic.subject}</span>
                      </td>
                      {/* <td className={'topic-cell topic-last-author-cell'}>
                        <span className={'topic-last-author'}>
                          {topic.lastMessageSenderType === 'OPERATOR'
                            ? (topic.lastMessageSenderName || 'Operator')
                            : (topic.accountName || 'Customer')}
                        </span>
                      </td> */}
                      <td className={'topic-cell topic-operator-cell'}>
                        <span className={'topic-operator'}>{topic.ownerOperator?.name || '-'}</span>
                      </td>
                      <td className={'topic-cell topic-customer-cell'}>
                        {topic.accountNumber ? (
                          <Button
                            className={'link-button topic-customer-link'}
                            onClick={(e) => handleRedirectToCustomer(e, topic.accountNumber)}
                            isDisabled={false}
                          >
                            {topic.accountName || topic.accountNumber}
                          </Button>
                        ) : (
                          <span className={'topic-customer'}>{topic.accountName || '-'}</span>
                        )}
                      </td>
                      <td className={'topic-cell topic-customer-status-cell'}>
                        <span className={'topic-customer-status'}>{topic.accountStatus || '-'}</span>
                      </td>
                      <td className={'topic-cell topic-time-cell'}>
                        <div className={'topic-time-wrapper'}>
                          <span className={'topic-time'}>{time}</span>
                          <span className={'topic-author'}>
                            {topic.lastMessageSenderType === 'OPERATOR' ? 'Author' : topic.accountName || 'Customer'}
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        ) : (
          <div className={'no-topics'}>
            <div className={'no-topics-content'}>
              <h3>
                {messagesStore.hasActiveFilters()
                  ? i18n.getMessage('messages.noTopics.noResults') || 'No topics match your filters'
                  : i18n.getMessage('messages.noTopics.title') || 'No messages yet'}
              </h3>
              <p>
                {messagesStore.hasActiveFilters()
                  ? i18n.getMessage('messages.noTopics.tryDifferentFilters') ||
                    'Try adjusting your filters to see more topics'
                  : i18n.getMessage('messages.noTopics.description') || 'Start a conversation by writing a new message'}
              </p>
            </div>
          </div>
        )}
      </div>
      {topics.length > 0 && (
        <Pagination
          isShowPaginationSize={false}
          currentPage={messagesStore.pagination.page}
          numberOfPages={messagesStore.totalPages()}
          setCurrentPage={handlePageChange}
          isLoading={messagesStore.isLoading}
        />
      )}
    </div>
  );
};

Topics.propTypes = {
  topics: PropTypes.array.isRequired,
  activeTopicIndex: PropTypes.number.isRequired,
  handleChangeActiveTopic: PropTypes.func.isRequired,
  handleClickNewMessageButton: PropTypes.func.isRequired,
  topicsRef: PropTypes.any.isRequired,
  showWriteMessageButton: PropTypes.bool.isRequired,
  messagesStore: MobXPropTypes.observableObject
};

export default inject((stores) => ({
  messagesStore: stores.messagesStore
}))(observer(Topics));
